import * as React from "react"
import styled, { css } from "styled-components"

import Typography from "~/components/atoms/Typography"
import ActionPairButton from "~/components/molecules/ActionPairButton"
import {
  Color,
  IsMobile,
  LineHeight,
  RadiusSize,
  Shadow,
  SpacingSize,
  TextSize,
  Transition,
} from "~/consts/style-mixins"
import { EventSchedule } from "~/entities/EventSchedule"
import { ShortInternshipSchedule } from "~/entities/ShortInternshipSchdule"

interface Props {
  open: boolean
  schedules: Array<ShortInternshipSchedule | EventSchedule>
  updateSelectedScheduleId: React.Dispatch<number>
  mainAction: {
    label: string
    action: Function
  }
  subAction: {
    label: string
    action: Function
  }
  entryIds?: number[]
}

interface StyledProps {
  open?: boolean
  selected?: boolean
  isEntry?: boolean
}

const { useState } = React

const ScheduleSelectModal = (props: Props) => {
  const { open, schedules, updateSelectedScheduleId, mainAction, subAction, entryIds } = props
  const [selectedScheduleId, selectSchedule] = useState<number>()
  const [disabledButton, toggleButtonState] = useState(true)

  return (
    <>
      <StyledDialogWrapper open={open}>
        <StyledDialogHeading>日程を選択してください</StyledDialogHeading>
        <StyledDialogContent>
          <StyledDescription>
            {schedules.filter((schedule) => schedule.beforeEntryDeadline || schedule.entryDeadlineNote).length === 0
              ? "エントリー可能な日程がありません"
              : "エントリーする日程を選択してください。"}
          </StyledDescription>
          <StyledSchedulesWrapper>
            {schedules.map((schedule, index) => {
              const isEntry = entryIds?.includes(schedule.id)
              if (
                schedule.capacity !== schedule.numberOfApplications &&
                (schedule.beforeEntryDeadline || schedule.entryDeadlineNote)
              ) {
                return (
                  <StyledEachSchedule
                    key={index}
                    selected={schedule.id === selectedScheduleId}
                    onClick={() => {
                      if (isEntry) {
                        return
                      }
                      selectSchedule(schedule.id)
                      toggleButtonState(false)
                      updateSelectedScheduleId(schedule.id)
                    }}
                    isEntry={isEntry}
                  >
                    {isEntry && <StyledDisabledEachSchedule>エントリー済み</StyledDisabledEachSchedule>}
                    {schedule.startsOn && schedule.endsOn ? (
                      <StyledDate type="LargeLabel">
                        {schedule.startsOn}〜{schedule.endsOn}
                      </StyledDate>
                    ) : (
                      <StyledDate type="LargeLabel">{schedule.scheduleNote}</StyledDate>
                    )}
                    <StyledPlace>{schedule.place}</StyledPlace>
                    <StyledAddress>{schedule.address}</StyledAddress>
                  </StyledEachSchedule>
                )
              }
            })}
          </StyledSchedulesWrapper>
        </StyledDialogContent>
        <StyledActionPairButtonWrapper>
          <ActionPairButton
            mainAction={{
              label: mainAction.label,
              action: mainAction.action,
              disabled: disabledButton,
            }}
            subAction={{
              label: subAction.label,
              action: subAction.action,
            }}
          />
        </StyledActionPairButtonWrapper>
      </StyledDialogWrapper>
      <StyledDialogOverlay open={open} />
    </>
  )
}

const StyledDialogWrapper = styled.div`
  width: calc(100% - 32px);
  max-width: 400px;
  margin: auto;
  padding: ${SpacingSize.XL}px;
  display: none;
  position: fixed;
  top: ${SpacingSize["3XL"]}px;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: ${Color.White};
  border-radius: ${RadiusSize.Medium}px;
  ${Shadow.Deep};
  ${({ open }: StyledProps) => css`
    ${open &&
    css`
      display: block;
    `}
  `}
`

const StyledDialogOverlay = styled.div`
  position: fixed;
  z-index: 99;
  top: -25%;
  left: 0;
  bottom: 0;
  right: 0;
  height: 125%;
  width: 100%;
  background: #000;
  display: none;
  opacity: 0.6;
  ${({ open }: StyledProps) => css`
    ${open &&
    css`
      display: block;
    `}
  `}
`

const StyledDialogHeading = styled.div`
  margin-bottom: ${SpacingSize["3XS"]}px;
  font-size: ${TextSize.L}px;
  font-weight: bold;
  line-height: ${LineHeight.S}%;
`

const StyledDialogContent = styled.div`
  margin-bottom: ${SpacingSize.XL}px;
  font-size: ${TextSize.S}px;
  line-height: ${LineHeight.M}%;
`

const StyledDescription = styled.div`
  margin-bottom: ${SpacingSize.XL}px;
  font-size: ${TextSize.S}px;
  line-height: ${LineHeight.M}%;
  color: ${Color.Gray};
`

const StyledSchedulesWrapper = styled.div`
  max-height: 60vh;
  overflow: scroll;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`

const StyledEachSchedule = styled.div`
  position: relative;
  margin-bottom: ${SpacingSize.XS}px;
  &:last-child {
    margin-bottom: 0;
  }
  padding: ${SpacingSize.M}px ${SpacingSize.M}px 13px;
  border-width: 1px;
  border-style: solid;
  border-color: ${Color.BorderGray};
  border-radius: ${RadiusSize.Medium}px;
  ${Transition.Hover};
  cursor: ${({ isEntry }) => (isEntry ? "not-allowed" : "pointer")};
  &:hover {
    border-color: ${({ isEntry }) => (isEntry ? Color.BorderGray : Color.Primary)};
  }
  ${({ selected }: StyledProps) =>
    selected &&
    css`
      background-color: ${Color.Primary};
      border-color: ${Color.BorderGray};
      div {
        color: ${Color.White} !important;
      }
    `}
`

const StyledDisabledEachSchedule = styled.div`
  position: absolute;
  user-select: none;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.85;
  background-color: ${Color.White};
  display: grid;
  place-items: center;
  font-size: 16px;
  font-weight: bold;
  color: ${Color.Black};
  border-radius: inherit;
`

const StyledDate = styled(Typography)`
  margin-bottom: 6px;
  line-height: ${LineHeight.S}%;
`

const StyledPlace = styled.div`
  margin-bottom: 6px;
  font-size: ${TextSize.S}px;
  font-weight: bold;
  color: ${Color.Gray};
  line-height: ${LineHeight["2XS"]}%;
`

const StyledAddress = styled.div`
  font-size: ${TextSize.S}px;
  color: ${Color.Gray};
  line-height: 130%;
`

const StyledActionPairButtonWrapper = styled.div`
  max-width: 320px;
  margin: auto;
  ${IsMobile(css`
    max-width: 280px;
  `)}
`

export default ScheduleSelectModal
